import React, { useState, useEffect, useCallback } from 'react';
import { X, Wand2, Save, Upload, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { previewSalesFile, getSalesMappingProfiles, saveSalesMappingProfile } from '../../config/api';

// Lets the user map an uploaded file's columns to the canonical sales-record
// fields before importing, and save/reuse per-client mapping profiles.
const ColumnMappingModal = ({ file, isOpen, onClose, onImport, importing }) => {
  const [loading, setLoading] = useState(false);
  const [headers, setHeaders] = useState([]);
  const [fields, setFields] = useState([]);
  const [sampleRows, setSampleRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [profiles, setProfiles] = useState([]);
  const [profileName, setProfileName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!file) return;
    setLoading(true);
    try {
      const [preview, profs] = await Promise.all([previewSalesFile(file), getSalesMappingProfiles().catch(() => [])]);
      setHeaders(preview.headers || []);
      setFields(preview.fields || []);
      setSampleRows(preview.sampleRows || []);
      setMapping(preview.suggestedMapping || {});
      setProfiles(profs || []);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not read the file');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [file, onClose]);

  useEffect(() => { if (isOpen && file) load(); }, [isOpen, file, load]);

  if (!isOpen) return null;

  const setField = (key, header) => setMapping((m) => ({ ...m, [key]: header || undefined }));
  const applyProfile = (id) => {
    const p = profiles.find((x) => x.id === id);
    if (p) { setMapping(p.mapping || {}); toast.success(`Applied “${p.name}”`); }
  };

  const requiredMissing = fields.filter((f) => f.required && !mapping[f.key]).map((f) => f.label);
  const mappedCount = fields.filter((f) => mapping[f.key]).length;

  const handleSaveProfile = async () => {
    if (!profileName.trim()) { toast.error('Name the profile first'); return; }
    setSaving(true);
    try {
      await saveSalesMappingProfile({ name: profileName.trim(), mapping });
      toast.success('Mapping profile saved');
      setProfiles(await getSalesMappingProfiles().catch(() => profiles));
      setProfileName('');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !importing && onClose()}>
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Map Columns</h3>
          </div>
          <button onClick={onClose} disabled={importing} title="Close" className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <p className="mb-3 text-sm text-gray-600">
            {file?.name} — match your file’s columns to the fields below. We’ve auto-detected what we could;
            adjust anything that’s off, then import.
          </p>

          {/* Profile picker */}
          {profiles.length > 0 && (
            <div className="mb-4 flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2">
              <span className="text-sm text-gray-500">Apply a saved profile:</span>
              <select onChange={(e) => applyProfile(e.target.value)} defaultValue=""
                className="rounded-md border border-gray-300 px-2 py-1 text-sm">
                <option value="" disabled>Choose…</option>
                {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}

          {loading ? (
            <div className="py-12 text-center text-gray-400">Reading file…</div>
          ) : (
            <>
              <div className="mb-2 flex items-center gap-3 text-xs">
                <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle className="h-3.5 w-3.5" /> {mappedCount} of {fields.length} mapped</span>
                {requiredMissing.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-amber-700"><AlertCircle className="h-3.5 w-3.5" /> Required unmapped: {requiredMissing.join(', ')}</span>
                )}
              </div>

              <div className="overflow-hidden rounded-lg border border-gray-100">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Field</th>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Your column</th>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Sample</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {fields.map((f) => {
                      const chosen = mapping[f.key] || '';
                      const sample = chosen && sampleRows[0] ? sampleRows[0][chosen] : undefined;
                      return (
                        <tr key={f.key}>
                          <td className="px-4 py-2 text-sm text-gray-800">
                            {f.label}{f.required && <span className="ml-1 text-red-500">*</span>}
                          </td>
                          <td className="px-4 py-2">
                            <select
                              value={chosen}
                              onChange={(e) => setField(f.key, e.target.value)}
                              className={`w-full rounded-md border px-2 py-1 text-sm ${f.required && !chosen ? 'border-amber-300 bg-amber-50' : 'border-gray-300'}`}
                            >
                              <option value="">— not mapped —</option>
                              {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                            </select>
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-400 truncate max-w-[160px]">{sample !== undefined ? String(sample) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Save profile */}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <input
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  placeholder="Save this mapping as… (e.g. McKesson East)"
                  className="flex-1 min-w-[220px] rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
                <button onClick={handleSaveProfile} disabled={saving}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                  <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save profile'}
                </button>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 px-5 py-4">
          <button onClick={onClose} disabled={importing} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          <button
            onClick={() => onImport(mapping)}
            disabled={loading || importing || requiredMissing.length > 0}
            title={requiredMissing.length > 0 ? 'Map the required fields first' : 'Import with this mapping'}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            <Upload className="h-4 w-4" /> {importing ? 'Importing…' : 'Import with this mapping'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ColumnMappingModal;
